import { useState, useEffect } from 'react';
import { Archive, Check, Clock, Eye } from 'lucide-react';
import { getInquiries, updateInquiryStatus } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const InquiriesManagement = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewingInquiry, setViewingInquiry] = useState(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getInquiries(params);
      setInquiries(res.data);
    } catch (err) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [filter, debouncedSearch]);

  const handleStatusChange = async (id, status) => {
    try {
      await updateInquiryStatus(id, status);
      toast.success('Status updated');
      fetchInquiries();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const statusIcons = {
    new: <Clock className="w-4 h-4" />,
    contacted: <Check className="w-4 h-4" />,
    closed: <Archive className="w-4 h-4" />,
  };

  const statusColors = {
    new: 'bg-ubud-jungle text-white',
    contacted: 'bg-temple-gold text-white',
    closed: 'bg-charcoal-muted text-white',
  };

  return (
    <div data-testid="admin-inquiries">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-semibold text-charcoal">Inquiries</h1>
        <div className="flex gap-2 flex-1 sm:flex-none sm:w-auto max-w-md">
          <Input
            placeholder="Search by code, name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="inquiry-search-input"
            className="flex-1"
          />
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Re / Message</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
              </TableRow>
            ) : inquiries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">No inquiries</TableCell>
              </TableRow>
            ) : (
              inquiries.map((inq) => (
                <TableRow
                  key={inq.id}
                  className="cursor-pointer hover:bg-warm-sand/40"
                  onClick={() => setViewingInquiry(inq)}
                  data-testid={`inquiry-row-${inq.id}`}
                >
                  <TableCell>
                    {inq.listing_code ? (
                      <span className="font-mono text-xs bg-warm-sand px-2 py-1 rounded">
                        {inq.listing_code}
                      </span>
                    ) : (
                      <span className="text-charcoal-muted text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium text-charcoal">{inq.name}</div>
                      <div className="text-charcoal-muted text-xs">{inq.email}</div>
                      {inq.phone && <div className="text-charcoal-muted text-xs">{inq.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{inq.inquiry_type}</TableCell>
                  <TableCell>
                    {inq.property_name && (
                      <div className="text-ubud-jungle text-xs font-medium mb-1">Re: {inq.property_name}</div>
                    )}
                    <div className="max-w-xs truncate text-sm">{inq.message}</div>
                  </TableCell>
                  <TableCell className="text-sm text-charcoal-muted">
                    {new Date(inq.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <span className={`${statusColors[inq.status]} text-xs px-2 py-1 rounded-full flex items-center gap-1 w-fit`}>
                      {statusIcons[inq.status]}
                      {inq.status}
                    </span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingInquiry(inq)}
                        data-testid={`inquiry-view-btn-${inq.id}`}
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Select value={inq.status} onValueChange={(v) => handleStatusChange(inq.id, v)}>
                        <SelectTrigger className="w-28 h-8 text-xs" data-testid={`inquiry-status-select-${inq.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Inquiry Detail Modal */}
      <Dialog open={!!viewingInquiry} onOpenChange={(open) => !open && setViewingInquiry(null)}>
        <DialogContent className="max-w-2xl" data-testid="inquiry-detail-modal">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>
          {viewingInquiry && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Name</div>
                  <div className="font-medium text-charcoal">{viewingInquiry.name}</div>
                </div>
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Type</div>
                  <div className="capitalize">{viewingInquiry.inquiry_type}</div>
                </div>
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Email</div>
                  <a href={`mailto:${viewingInquiry.email}`} className="text-ubud-jungle hover:underline">{viewingInquiry.email}</a>
                </div>
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Phone</div>
                  {viewingInquiry.phone ? (
                    <a href={`tel:${viewingInquiry.phone}`} className="text-ubud-jungle hover:underline">{viewingInquiry.phone}</a>
                  ) : <span className="text-charcoal-muted">—</span>}
                </div>
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Status</div>
                  <span className={`${statusColors[viewingInquiry.status]} text-xs px-2 py-1 rounded-full inline-flex items-center gap-1`}>
                    {statusIcons[viewingInquiry.status]}
                    {viewingInquiry.status}
                  </span>
                </div>
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Submitted</div>
                  <div>{new Date(viewingInquiry.created_at).toLocaleString()}</div>
                </div>
              </div>

              {(viewingInquiry.listing_code || viewingInquiry.property_name || viewingInquiry.property_location) && (
                <div className="border-t pt-4">
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-2">Related Property</div>
                  <div className="bg-warm-sand rounded-lg p-3 space-y-1">
                    {viewingInquiry.listing_code && (
                      <div className="font-mono text-xs">{viewingInquiry.listing_code}</div>
                    )}
                    {viewingInquiry.property_name && (
                      <div className="font-medium text-charcoal">{viewingInquiry.property_name}</div>
                    )}
                    {viewingInquiry.property_location && (
                      <div className="text-charcoal-muted text-xs">{viewingInquiry.property_location}</div>
                    )}
                  </div>
                </div>
              )}

              {viewingInquiry.property_location && !viewingInquiry.property_name && (
                <div>
                  <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-1">Property Location of Interest</div>
                  <div>{viewingInquiry.property_location}</div>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="text-charcoal-muted text-xs uppercase tracking-wide mb-2">Message</div>
                <div className="bg-warm-sand rounded-lg p-3 whitespace-pre-wrap text-charcoal">
                  {viewingInquiry.message || <span className="text-charcoal-muted italic">No message provided</span>}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {viewingInquiry && (
              <Select
                value={viewingInquiry.status}
                onValueChange={async (v) => {
                  await handleStatusChange(viewingInquiry.id, v);
                  setViewingInquiry({ ...viewingInquiry, status: v });
                }}
              >
                <SelectTrigger className="w-40" data-testid="inquiry-detail-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" onClick={() => setViewingInquiry(null)} data-testid="inquiry-detail-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InquiriesManagement;
